import { useEffect, type ReactNode } from 'react';
import { navigate } from '../router';

export function TopBar({ title, back, right }: { title: string; back?: string | (() => void); right?: ReactNode }) {
  return (
    <div className="topbar">
      {back ? (
        <button className="icon-btn" aria-label="Back" onClick={() => (typeof back === 'string' ? navigate(back) : back())}>
          <svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      ) : <span className="icon-spacer" />}
      <h1>{title}</h1>
      <div className="topbar-right">{right}</div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

export function Loading({ label = 'Loading…' }: { label?: string }) { return <div className="loading" role="status">{label}</div>; }
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <span>{message}</span>
      {onRetry && <button className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Pill({ kind, children }: { kind: 'ok' | 'warn' | 'bad' | 'info' | 'muted'; children: ReactNode }) {
  return <span className={`pill pill-${kind}`}>{children}</span>;
}

export function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const labels = ['Select Employees', 'Settings', 'Preview'];
  return (
    <ol className="stepper" aria-label="Drawing setup steps">
      {labels.map((l, i) => (
        <li key={l} className={i + 1 === step ? 'active' : i + 1 < step ? 'done' : ''} aria-current={i + 1 === step ? 'step' : undefined}>
          <span className="dot">{i + 1 < step ? '✓' : i + 1}</span><span className="lbl">{l}</span>
        </li>
      ))}
    </ol>
  );
}

export function Modal({ title, children, onClose, actions }: { title: string; children: ReactNode; onClose: () => void; actions: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <div className="modal-body">{children}</div>
        <div className="modal-actions">{actions}</div>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) { return <p className="empty">{children}</p>; }
